// Framework and third-party non-ui
import React, { useEffect, useRef, useState } from "react";

import { I18nextProvider, useTranslation } from "react-i18next";
import instance from "../../utils/i18n";

// Component specific modules (Component-styled, etc.)
import {
  StyledDetailsGrid,
  StyledCardSection,
  StyledMapSection,
  StyledLocationTitle,
  StyledSectionHeading,
  StyledFieldSection,
  DetailLocationTextLine,
} from "./Details-styled";
import { DetailsProps } from "./Details.types";
import { _getLocationById } from "./DetailsHelper";

// App and lib components
import {
  Error as ErrorComponent,
  ErrorTypes,
  FooterBar,
  Map,
  MapController,
} from "../../components"; // TODO - fix circular dependencies
import {
  getDistanceFromFocalPoint,
  onOpenDirections,
  roundToTwo,
  updateDomainFields,
} from "../../utils";
import { LocationTextLine, LocationTitleIcons } from "../shared";

// Third-party components (buttons, icons, etc.)
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import MapView from "@arcgis/core/views/MapView";

import "@esri/calcite-components/dist/components/calcite-button";
import "@esri/calcite-components/dist/components/calcite-modal";
import { CalciteAction, CalciteButton, CalciteIcon, CalciteModal } from "@esri/calcite-components-react";
import { StyledDetailSpan, StyledLabelSpan, StyledPhoneLink, StyledWebsiteLink } from "../LocationCard/LocationCard-styled";

import LocationBadges from "../LocationBadge/LocationBadge";
import { getLocationBadgePropsFromGraphic } from "../LocationBadge/getLocationBadgeProps";

const initialScale = 36111.909643; // LOD 14: https://developers.arcgis.com/documentation/mapping-apis-and-services/reference/zoom-levels-and-scale/
const detailsScale = 18055;

const Details = ({
  locationId,
  focalPoint,
  locationsFeatureLayer,
  updateMapHighlight,
  locationLayerInfo,
  locationLayerUrl,
  mapId,
  onClose,
  onZoomTo,
  onCardClick,
  onPopupUpdate,
  updateGoogleTranslate,
  fieldLabels,
}: DetailsProps) => {
  const [loc, setLoc] = useState<Graphic | undefined>();
  const [detailMapView, setDetailMapView] = useState<MapView | undefined>();
  const [proximity, setProximity] = useState<number | undefined>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [locQueryReturned, setLocQueryReturned] = useState<boolean>(false);
  const [bannerMapView, setBannerMapView] = useState<MapView | undefined>();

  const { t, i18n } = useTranslation("components", { i18n: instance });

  const titleElement = useRef<HTMLHeadingElement>(null);
  const modalElement = useRef<HTMLCalciteModalElement>(null);

  // MySchools URL (only one will exist, sometimes none)
  const mySchoolsUrlRaw = String(loc?.getAttribute?.("MYSCHOOLS_URL") ?? "").trim();

  const mySchoolsUrl =
    mySchoolsUrlRaw.length > 0
      ? mySchoolsUrlRaw.toLowerCase().startsWith("http")
        ? mySchoolsUrlRaw
        : `https://${mySchoolsUrlRaw}`
      : "";


const ocfsInspectUrlRaw = String(loc?.getAttribute?.("OCFS_INSPECTURL") ?? "").trim();
const dohmhInspectUrlRaw = String(loc?.getAttribute?.("DOHMH_INSPECTION_URL") ?? "").trim();

const inspectUrlRaw = ocfsInspectUrlRaw || dohmhInspectUrlRaw;

const inspectUrl =
  inspectUrlRaw.length > 0
    ? inspectUrlRaw.toLowerCase().startsWith("http")
      ? inspectUrlRaw
      : `https://${inspectUrlRaw}`
    : "";
  //console.log(`ocfsInspectUrlRaw: ${ocfsInspectUrlRaw}, dohmhInspectUrlRaw: ${dohmhInspectUrlRaw}, inspectUrlRaw: ${inspectUrlRaw}, inspectUrl: ${inspectUrl}`);

  const ageMin = loc?.getAttribute?.("AGEMIN_YEAR");
  const ageMax = loc?.getAttribute?.("AGEMAX_YEAR");
  const agesText =
    ageMin != null && ageMax != null ? `${ageMin} to ${ageMax} ` : "";


  const programUrlRaw = String(loc?.getAttribute?.("CARETYPE_URL") ?? "").trim();

  const programUrl =
    programUrlRaw.length > 0
      ? programUrlRaw.toLowerCase().startsWith("http")
        ? programUrlRaw
        : `https://${programUrlRaw}`
      : "";

  const programName = loc?.getAttribute?.("CARETYPE_NAME")

  const hoursText = loc?.getAttribute?.("TYPEHOURS") ?? ""; 
  const yearlyScheduleText = loc?.getAttribute?.("YEARSCHEDULE") ?? ""; 
  const eligibilityText = loc?.getAttribute?.("ELIGIBILITY") ?? ""; 

  // query for specific location in case user arrives at this view first
  useEffect(() => {
    const getLocation = async () => {
      if (locationId && locationsFeatureLayer) {
        try {
          const locResult = await _getLocationById(parseInt(locationId), locationsFeatureLayer);
          setLocQueryReturned(true);
          if (locationsFeatureLayer && locationLayerInfo.domainFieldNames) {
            updateDomainFields(
              [locResult],
              locationsFeatureLayer,
              locationLayerInfo.domainFieldNames
            );
          }
          setLoc(locResult);

          if (!locResult) {
            setTimeout(() => {
              onClose();
            }, 4000);
          }

          if (focalPoint) {
            setProximity(roundToTwo(getDistanceFromFocalPoint(focalPoint, locResult)) || undefined);
          }
        } catch (error) {
          console.warn(error);
          setTimeout(() => {
            onClose();
          }, 4000);
        }
      }
    };
    getLocation();
    // excluding servicesActive from the dependency array to avoid infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, locationsFeatureLayer, locationLayerInfo.serviceTypeFieldNames]);

  const setFocus = () => {
    if (titleElement && titleElement.current) {
      titleElement.current.focus();
    }
  };
  useEffect(() => {
    if (modalOpen) {
      setTimeout(() => {
        setFocus();
      }, 300);
    }
  }, [modalOpen]);

  useEffect(() => {
    setFocus();
    if (detailMapView && loc) {
     
      MapController.goToLocationAndShowPin({
        mapView: detailMapView,
        layerUrl: `${locationLayerUrl}`,
        pinPoint: loc,
        pinImageUrl: `${process.env.PUBLIC_URL}/mappin.svg`,
        updateMapHighlight,
        objectIdFieldname: locationLayerInfo.objectIdFieldName,
      });
    }
  }, [
    detailMapView,
    loc,
    locationLayerInfo.objectIdFieldName,
    locationLayerUrl,
    updateMapHighlight,
  ]);

  useEffect(() => {
    updateGoogleTranslate();
  }, [loc, updateGoogleTranslate]);

  useEffect(() => {
    if (modalElement && modalElement.current) {
      setModalOpen(true);
    }
  }, [loc]);

  const initialCenter = loc ? (loc.geometry as Point) : new Point({ x: 0, y: 0 });
  const locIsAccessible =
    locationLayerInfo.accessibleFieldName && locationLayerInfo.accessibleFieldValue
      ? loc?.attributes[locationLayerInfo.accessibleFieldName] ===
        locationLayerInfo.accessibleFieldValue
      : false;

  const locIsPetFriendly =
    locationLayerInfo.petFriendlyFieldName && locationLayerInfo.petFriendlyFieldValue
      ? loc?.attributes[locationLayerInfo.petFriendlyFieldName] ===
        locationLayerInfo.petFriendlyFieldValue
      : false;

  const locAllAges =
    locationLayerInfo.allAgesFieldName && locationLayerInfo.allAgesFieldValue
      ? loc?.attributes[locationLayerInfo.allAgesFieldName] === locationLayerInfo.allAgesFieldValue
      : false;

  const badgeProps = loc
    ? getLocationBadgePropsFromGraphic(loc, locationLayerInfo)
    : { locType: undefined, costType: undefined, careType: undefined };


  const handleDirectionsClick = () => {
    onOpenDirections(loc, focalPoint);
  };

  // Force the banner map to resize once the modal is actually open/rendered
  useEffect(() => {
    if (!modalOpen || !bannerMapView) return;

    // Give layout a beat to settle, then resize the view to its container
    requestAnimationFrame(() => {
      try {
        bannerMapView.resize();
      } catch {}
    });
  }, [modalOpen, bannerMapView]);

  // Remove widgets/legend/etc from the banner map
  useEffect(() => {
  if (!bannerMapView) return;

  bannerMapView.when(() => {
      try {
        // Remove all ArcGIS UI widgets
        bannerMapView.ui.components = [];

        bannerMapView.ui.empty("top-left");
        bannerMapView.ui.empty("top-right");
        bannerMapView.ui.empty("bottom-left");
        bannerMapView.ui.empty("bottom-right");

        // Disable interactions
        bannerMapView.popupEnabled = false;
        bannerMapView.navigation.mouseWheelEnabled = false;
        bannerMapView.navigation.dragEnabled = false;
      } catch {}
    });
  }, [bannerMapView]);

  return (
    <I18nextProvider i18n={i18n}>
      {!loc && locQueryReturned && <ErrorComponent message={ErrorTypes.LOCATION} />}
      {loc && (
        <CalciteModal
          id="details-modal"
          aria-labelledby={titleElement?.current?.id || ""}
          ref={modalElement}
          open={modalOpen}
          closeButtonDisabled
          onCalciteModalClose={onClose}
          style={
            {
            "--calcite-modal-header-padding": "0px",
            "--calcite-modal-content-padding": "0px",
            } as React.CSSProperties
          }
        >
          {/* Card */}
          <StyledCardSection slot="header">
            <div className="details-header">
              {/* Full-width map banner */}
              <div className="details-map-banner" aria-hidden="true">
                <div className="details-map-inner">
                  <Map
                    itemId={mapId}
                    initialScale={detailsScale}
                    initialCenter={initialCenter}
                    targetLayerUrl={locationLayerUrl}
                    locationsFeatureLayerInfo={locationLayerInfo}
                    onError={() => null}
                    onMapViewSet={setBannerMapView}
                    isDetails={true}
                    locationGraphicsLayerId={MapController.SELECTED_LOCATION_LAYER_ID}
                    onCardClick={onCardClick}
                    onPopupUpdate={onPopupUpdate}
                    updateGoogleTranslate={updateGoogleTranslate}
                    fieldLabels={fieldLabels}
                  />
                </div>
              {/* ONE close button, absolutely positioned */}
                <CalciteButton
                  className="details-close-btn details-icon-btn"
                  appearance="solid"
                  kind="neutral"
                  round
                  scale="m"
                  label="Close"
                  onClick={onClose}
                >
                  <span className="details-close-x" aria-hidden="true">
                    ×
                  </span>
                </CalciteButton>
              </div>
              {/* Header content */}
              <div className="details-header-content">
                <div className="details-title-block">
                  <StyledLocationTitle ref={titleElement} tabIndex={0}>
                    {loc.attributes[locationLayerInfo.titleFieldName]}
                    <LocationTitleIcons
                      isAccessible={locIsAccessible}
                      isPetFriendly={locIsPetFriendly}
                      allAges={locAllAges}
                    />
                  </StyledLocationTitle>

                  <div className="details-badges">
                    <LocationBadges locType={badgeProps.locType} costType={badgeProps.costType} careType={badgeProps.careType} />
                  </div>
                </div>

                <div className="details-meta-row">
                  <div className="details-meta-left">
                    <div className="details-address">
                      {loc.attributes[locationLayerInfo.addressFieldName]}, {loc.getAttribute("CITY")}{" "}
                      {loc.getAttribute("STATE")}, {loc.getAttribute("ZIPCODE")}
                    </div>

                    {proximity && (
                      <div className="details-distance">
                        <CalciteIcon icon="pin-tear-f" scale="m" />
                        {`${proximity} Miles Away`}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <CalciteButton
                      className="details-directions-btn details-icon-btn"
                      appearance="outline"
                      kind="brand"
                      iconStart="road-sign"
                      round
                      scale="m"
                      onClick={handleDirectionsClick}
                      label="Directions"
                    />
                    <span style={{ fontSize: "13px", color: "var(--app-primary-color)", marginTop: "2px", fontWeight: 500 }}>
                      Directions
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </StyledCardSection>

          <StyledDetailsGrid slot={"content"}>
            {/* Detail sections */}
            <StyledFieldSection>
              <StyledSectionHeading>Care details</StyledSectionHeading>

              <div className="details-care-grid">
                {/* Ages */}
                <div className="details-care-item">
                  <CalciteIcon icon="speech-bubble-like" scale="s" />
                  <div className="details-care-text">
                    <div className="details-care-label">Ages</div>
                    <div className="details-care-value">{agesText || "—"}</div>
                  </div>
                </div>

                {/* Hours */}
                <div className="details-care-item">
                  <CalciteIcon icon="clock" scale="s" />
                  <div className="details-care-text">
                    <div className="details-care-label">Hours</div>
                    <div className="details-care-value">{hoursText || "—"}</div>
                  </div>
                </div>

                {/* Eligibility */}
                <div className="details-care-item">
                  <CalciteIcon icon="check-circle" scale="s" />
                  <div className="details-care-text">
                    <div className="details-care-label">Eligibility</div>
                    <div className="details-care-value">
                      {(String(eligibilityText ?? "").trim().toLowerCase() === "based on income and need" || String(eligibilityText ?? "").trim().toLowerCase() === "based on income or need") && programUrl ? (
                        <>
                          Based on{" "}
                          <a href={programUrl} target="_blank" rel="noreferrer">
                            income or need
                          </a>
                        </>
                      ) : (
                        eligibilityText || "—"
                      )}
                    </div>
                  </div>
                </div>

                {/* Yearly schedule */}
                <div className="details-care-item">
                  <CalciteIcon icon="calendar" scale="s" />
                  <div className="details-care-text">
                    <div className="details-care-label">Yearly schedule</div>
                    <div className="details-care-value">{yearlyScheduleText || "—"}</div>
                  </div>
                </div>

              </div>

              {programName && (
                <div className="details-program">
                  This program is called {" "}
                  {programUrl ? (
                    <a href={programUrl} target="_blank" rel="noreferrer">
                      {programName}
                    </a>
                  ) : (
                    <span>{programName}</span>
                  )}
                  .
                  {/* 2-K eligibility note */}
                  {typeof programName === "string" && programName.toLowerCase().includes("2-k") && (
                    <div style={{ marginTop: "6px", color: "#444", fontSize: "0.98em", fontStyle: "italic" }}>
                     Home-based child care providers allow any child born in 2024 to enroll in 2-K on the first day of school, September 10, 2026. 
                     <br></br><br></br>To enroll in 2-K at child care centers, children must be 2 years old on the first day of school. Children who turn 2 later in the year can start on their second birthday.
                    </div>
                  )}
                </div>
              )}
            </StyledFieldSection>

            {inspectUrl && (
              <StyledFieldSection>
                <StyledSectionHeading>Inspections</StyledSectionHeading>

                <DetailLocationTextLine>
                  <StyledLabelSpan>
                    See the most recent health and safety inspection reports for this provider
                  </StyledLabelSpan>
                </DetailLocationTextLine>

                <DetailLocationTextLine>
                  <StyledWebsiteLink>
                    <div className="inspection-link">
                      <a href={inspectUrl} target="_blank" rel="noreferrer">
                      <CalciteIcon icon="launch" scale="s" /> View inspections and permitting data
                      </a>
                    </div>
                  </StyledWebsiteLink>
                </DetailLocationTextLine>
              </StyledFieldSection>
            )}


          {/* CONTACT PROVIDER (only show if we have at least phone or MySchools) */}
          {(loc?.attributes?.[locationLayerInfo.phoneFieldName] || mySchoolsUrl) && (
            <StyledFieldSection>
              <div className="details-contact-card">
                <StyledSectionHeading>Contact provider</StyledSectionHeading>
                <div className="details-contact-copy">
                  <div className="details-contact-lede">
                    To learn more about this child care option, including how to enroll, contact provider
                    directly.
                  </div>

                  {/* Optional helper link — plug in the real URL when we have it */}
                  { <a
                    className="details-contact-tips"
                    href="https://nyc.gov/content/childcarehub/pages/frequently-asked-questions#tips-for-parents"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Review our tips on what to ask providers.
                  </a> }
                </div>

                <div className="details-contact-links">
                  {/* Phone */}
                  {locationLayerInfo.phoneFieldName &&
                    loc?.attributes?.[locationLayerInfo.phoneFieldName] && (
                      <a
                        className="details-contact-link contact-phone"
                        href={`tel:${String(loc.attributes[locationLayerInfo.phoneFieldName]).replace(/\s/g, "")}`}
                      >
                        <CalciteIcon icon="phone" scale="s" />
                        {loc.attributes[locationLayerInfo.phoneFieldName]}
                      </a>
                    )}

                  {/* MySchools (only if present) */}
                  {mySchoolsUrl && (
                    <a
                      className="details-contact-link"
                      href={mySchoolsUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <CalciteIcon icon="launch" scale="s" />
                      MySchools profile
                    </a>
                  )}
                </div>
              </div>
            </StyledFieldSection>
          )}
            <FooterBar footerType="details"></FooterBar>
          </StyledDetailsGrid>
        </CalciteModal>
      )}
    </I18nextProvider>
  );
};

export default Details;
